import React from 'react';
import {StyleSheet, View} from 'react-native';
import {BottomTabNavigationOptions} from '@react-navigation/bottom-tabs';

import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import type {AppRoutesNavigatorScreenProps} from '../models/navigationTypes';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';

import useCommonScreenOptions from './hooks/useCommonScreenOptions';
import {getRoutesForStoreType} from './routes/routes';
import {StoreType} from './routes/types';
import {Stack, Tab} from './navigators';

const EmptyComponent = () => <View />;

// BottomTabNavigator on Mobile, Stack Navigator on Web Desktop
const AppRoutesNavigator = (_: AppRoutesNavigatorScreenProps) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const {shopSelectionStore, userStore} = useStores();
  const isLoggedIn = userStore.isLoggedIn();

  const TabComponent = isWebDesktopScreen ? Stack : Tab;

  const commonScreenOptions = useCommonScreenOptions(true);
  const bottomTabOptions: BottomTabNavigationOptions = {
    tabBarIconStyle: styles.icon,
    tabBarItemStyle: styles.item,
    tabBarStyle: styles.tabBarStyle,
    tabBarShowLabel: false,
  };
  const screenOptions = {
    ...commonScreenOptions,
    ...(isWebDesktopScreen ? {} : bottomTabOptions),
  };

  const storeType = shopSelectionStore?.getSelectedStoreType();

  return (
    <TabComponent.Navigator
      backBehavior="history"
      // @ts-ignore
      screenOptions={screenOptions}
      initialRouteName={shopSelectionStore?.getHomeAppRoute()}
      // We may need to bottom tabs in the Gift Card Store. Just hiding them for now.
      tabBar={
        storeType === StoreType.GiftCardStore ? EmptyComponent : undefined
      }>
      {getRoutesForStoreType(
        storeType,
        shopSelectionStore.loyaltyEnabled,
        shopSelectionStore.ecommerceDisabled,
        isLoggedIn,
      ).map(
        ({
          name,
          component,
          icon: tabBarIcon,
          showInTab,
          header,
          initialParams,
          listeners,
        }) => (
          <TabComponent.Screen
            key={name} // react key
            name={name}
            component={component}
            initialParams={initialParams}
            listeners={listeners}
            // @ts-ignore
            options={{
              header,
              tabBarActiveBackgroundColor:
                theme.colour.background.general.primary,
              tabBarIcon,
              tabBarButton: !showInTab ? hiddenTabsTabBarButton : undefined,
            }}
          />
        ),
      )}
    </TabComponent.Navigator>
  );
};

const hiddenTabsTabBarButton = () => <View style={{display: 'none'}} />;

const styles = StyleSheet.create({
  tabBarStyle: {
    borderTopWidth: 0.5,
    borderTopColor: 'grey',
    paddingBottom: 0,
    height: 52,
  },
  icon: {
    flex: 1,
    width: '100%',
  },
  item: {
    justifyContent: 'center',
    width: '100%',
  },
});

export default AppRoutesNavigator;
